import React from "react";
import { Link } from "react-router-dom";
import { 
  Home, Building2, Piano, Truck, Package, 
  ArrowRight, CheckCircle, Phone, Clock, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import VideoHero from "@/components/VideoHero";

const SERVICES_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/7m7lw26g_1768944737752.jpg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/no7liyqw_1769062093447.png";
const TRUCK_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/c5to8wjs_1769061921315.png";

const ServicesPage = () => {
  const services = [
    {
      icon: Home,
      title: "Residential Moving",
      description: "Full-service home moving for apartments, houses, condos, and townhomes. From single rooms to entire homes.",
      features: [
        "Furniture disassembly & reassembly",
        "Careful packing & unpacking",
        "Local & long-distance moves",
        "Senior moving assistance"
      ],
      image: SERVICES_IMAGE
    },
    {
      icon: Building2,
      title: "Commercial Moving",
      description: "Office and business relocations with minimal downtime. We work around your schedule.",
      features: [
        "After-hours & weekend moves",
        "Office furniture & equipment",
        "IT equipment handling",
        "Minimal business disruption"
      ],
      image: HERO_IMAGE
    },
    {
      icon: Piano,
      title: "Specialty Items",
      description: "Expert handling of heavy, delicate, and valuable items that require special care and equipment.",
      features: [
        "Piano moving (upright & grand)",
        "Pool table relocation",
        "Antique furniture",
        "Safes & heavy equipment"
      ],
      image: TRUCK_IMAGE
    }
  ];

  const additionalServices = [
    {
      icon: Package,
      title: "Packing Services",
      description: "Professional packing using quality materials to protect your belongings."
    },
    {
      icon: Truck,
      title: "Loading & Unloading",
      description: "Need help with just the heavy lifting? We can load or unload your rental truck."
    },
    {
      icon: Clock,
      title: "Same-Day Moves",
      description: "Emergency move? We offer same-day service when available."
    }
  ];

  const pricingInfo = {
    crews: [
      { men: 2, rate: 165 },
      { men: 3, rate: 190 },
      { men: 4, rate: 210 },
    ],
    minimumHours: 2.5,
    includes: [
      "Professional moving crew",
      "Fully equipped moving truck",
      "Moving blankets & straps",
      "Full insurance coverage",
      "Door-to-door service"
    ],
    surcharges: [
      { name: "Out of Town", fee: "$100" },
      { name: "Pool Table", fee: "$400-600" },
      { name: "Piano (Upright)", fee: "$150" },
      { name: "Piano (Grand)", fee: "$300" },
      { name: "Hot Tub", fee: "$250" },
    ]
  };

  return (
    <div data-testid="services-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="Our Moving Services"
        subtitle="Professional moving services tailored to your needs. From small apartments to large homes, we've got you covered."
      >
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/quote" data-testid="services-quote-btn">
            <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8">
              Get Free Quote
            </Button>
          </Link>
          <a href="tel:613-362-8180">
            <Button className="bg-slate-600 hover:bg-slate-700 text-white font-bold text-lg py-6 px-8">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </Button>
          </a>
        </div>
      </VideoHero>

      {/* Main Services */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">What We Offer</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase">
              Full-Service Moving Solutions
            </h2>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-section-${index}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-[#0047AB]/10 p-3 rounded-lg">
                      <service.icon className="w-6 h-6 text-[#0047AB]" />
                    </div>
                    <h3 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-lg text-slate-600 mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#0047AB]" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/quote">
                    <Button className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold">
                      Get a Quote
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">More Options</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase">
              Additional Services
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 text-center"
                data-testid={`additional-service-${index}`}
              >
                <div className="bg-[#0047AB]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <service.icon className="w-8 h-8 text-[#0047AB]" />
                </div>
                <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">Simple Pricing</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 uppercase mb-4">
              Fair & Transparent Rates
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We charge by the hour with clear, upfront rates. No hidden fees, no surprises.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {pricingInfo.crews.map((crew, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-8 text-center ${
                  index === 1 
                    ? "bg-[#0047AB] text-white scale-105 shadow-xl" 
                    : "bg-white border-2 border-slate-200"
                }`}
              >
                {index === 1 && (
                  <span className="bg-slate-700 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 inline-block">
                    MOST POPULAR
                  </span>
                )}
                <p className={`text-lg font-semibold mb-2 ${index === 1 ? "text-white/90" : "text-slate-600"}`}>
                  {crew.men} Men + Truck
                </p>
                <p className={`text-5xl font-bold font-['Barlow_Condensed'] mb-1 ${index === 1 ? "text-white" : "text-slate-900"}`}>
                  ${crew.rate}
                </p>
                <p className={index === 1 ? "text-white/80" : "text-slate-500"}>per hour</p>
                <div className={`border-t mt-6 pt-6 ${index === 1 ? "border-white/20" : "border-slate-200"}`}>
                  <p className={`text-sm ${index === 1 ? "text-white/80" : "text-slate-500"}`}>
                    {pricingInfo.minimumHours} hour minimum
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* What's Included & Surcharges */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                What's Included
              </h3>
              <ul className="space-y-3">
                {pricingInfo.includes.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0047AB]" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-slate-200">
                <ShieldCheck className="w-8 h-8 text-[#0047AB]" />
                <div>
                  <p className="font-bold text-slate-900">Fully Insured</p>
                  <p className="text-sm text-slate-600">Your belongings are protected</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-8">
              <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                Specialty Surcharges
              </h3>
              <div className="space-y-3">
                {pricingInfo.surcharges.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                    <span className="text-slate-700">{item.name}</span>
                    <span className="font-bold text-slate-900">+{item.fee}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-6">
                *Specialty items require extra care, equipment, and time
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/quote">
              <Button className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold text-lg py-6 px-12">
                Get Your Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-white uppercase mb-6">
            Ready to Book Your Move?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contact us today for a free quote or book your move online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" data-testid="services-cta-quote">
              <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Get Free Quote
              </Button>
            </Link>
            <Link to="/booking" data-testid="services-cta-book">
              <Button className="bg-slate-600 hover:bg-slate-700 text-white font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Book Your Move
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
